import { ActionIcon, Collapse, Flex, Group, Stack, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import React, { PropsWithChildren, useEffect } from 'react';
import { CardHeader } from '../additional/CardHeader';
import { getDepth, isTopLevel } from '../../utils/navigation';
import { useDisclosure, useHover } from '@mantine/hooks';
import { GAP_BETWEEN_COLUMNS, LEFT_COLUMN_WIDTH } from '../constants/sizes';

export default function CollapsableSection({
  label,
  selected,
  required,
  children,
  description,
  path,
  additionalHeaderContent,
}: PropsWithChildren & {
  label?: string;
  selected: boolean;
  required: boolean;
  description?: string;
  path?: string;
  additionalHeaderContent?: React.ReactNode;
}) {
  const depth = getDepth(path);
  const topLevel = isTopLevel(depth);
  const [opened, { toggle, open }] = useDisclosure(true);
  const { hovered, ref } = useHover();

  useEffect(() => {
    if (selected) {
      open();
    }
  }, [selected, open]);

  if (topLevel) {
    return (
      <Stack ml='xs' gap={'xs'}>
        <Group gap={0}>
          <Stack miw={`${LEFT_COLUMN_WIDTH}%`} mr={GAP_BETWEEN_COLUMNS} gap={0} flex={LEFT_COLUMN_WIDTH}>
            <Text fz='h3' data-testid='collapsible-section-top-level-title'>
              {label}
            </Text>
            <Text c='gray.6' size='md' data-testid='collapsible-section-top-level-description'>
              {description}
            </Text>
          </Stack>
          {additionalHeaderContent && (
            <Flex flex={100 - LEFT_COLUMN_WIDTH} mt='xs' style={{ alignSelf: 'flex-start' }}>
              {additionalHeaderContent}
            </Flex>
          )}
        </Group>
        {children}
      </Stack>
    );
  }

  return (
    <Stack
      mx={-5}
      px={5}
      py={2}
      bg={hovered ? 'gray.0' : undefined}
      flex={1}
      mb='xs'
      gap='xs'
      style={{ borderRadius: 'var(--mantine-radius-default)', transition: 'background-color 0.3s' }}
    >
      <Group gap={0}>
        <Group wrap='nowrap' miw={`${LEFT_COLUMN_WIDTH}%`} gap='xs' mr={GAP_BETWEEN_COLUMNS} flex={LEFT_COLUMN_WIDTH}>
          <ActionIcon
            ml={-CHEVRON_DISPLACEMENT}
            style={{ alignSelf: 'flex-start' }}
            component='div'
            c='gray'
            bg='transparent'
            ref={ref}
          >
            <IconChevronDown onClick={toggle} size={20} style={{ rotate: opened ? '180deg' : '0deg' }} />
          </ActionIcon>
          <Stack gap={0}>
            <CardHeader title={label} path={path} checked={selected} required={required} />
            <Text c='gray.6' size='sm' data-testid='collapsible-section-description'>
              {description}
            </Text>
          </Stack>
        </Group>
        {additionalHeaderContent && (
          <Flex flex={100 - LEFT_COLUMN_WIDTH} mt='xs' style={{ alignSelf: 'flex-start' }}>
            {additionalHeaderContent}
          </Flex>
        )}
      </Group>

      <Collapse in={opened}>
        <Stack gap='xs'>{children}</Stack>
      </Collapse>
    </Stack>
  );
}

const CHEVRON_DISPLACEMENT = 36;
